import { debounce } from "lodash";
import { Boundaries, isCoordinates } from "../useMapCenter";
import { GeoResult, GeoService } from "./geoservices";

const convertBoundariesToGoogleBounds = (
  boundaries: Boundaries
): google.maps.LatLngBoundsLiteral => ({
  east: boundaries.ne[0],
  north: boundaries.ne[1],
  west: boundaries.sw[0],
  south: boundaries.sw[1]
});

const getNameFromResult = (result: google.maps.GeocoderResult): string => {
  const routeComponent = result.address_components.find((c) =>
    c.types.includes("route")
  );
  const streetNumber = result.address_components.find((c) =>
    c.types.includes("street_number")
  );
  const locality = result.address_components.find((c) =>
    c.types.includes("locality")
  );
  const sublocality = result.address_components.find((c) =>
    c.types.includes("sublocality")
  );

  if (streetNumber && routeComponent && (locality || sublocality))
    return [
      routeComponent.short_name,
      streetNumber.short_name,
      (locality ?? sublocality)?.short_name ?? ""
    ].join(", ");

  return result.formatted_address;
};

const SORT_PRIORITY: Record<string, number> = {
  ROOFTOP: 10,
  RANGE_INTERPOLATED: 20,
  APPROXIMATE: 30,
  GEOMETRIC_CENTER: 40
};

const sortResults = (
  resultA: google.maps.GeocoderResult,
  resultB: google.maps.GeocoderResult
): number => {
  const priorityA =
    SORT_PRIORITY[resultA.geometry.location_type] ?? Number.MAX_SAFE_INTEGER;
  const priorityB =
    SORT_PRIORITY[resultB.geometry.location_type] ?? Number.MAX_SAFE_INTEGER;

  return priorityA - priorityB;
};

const convertGeocoderResultsToGeoResult = (
  result: google.maps.GeocoderResult
): GeoResult => ({
  address: result.formatted_address,
  center: [result.geometry.location.lng(), result.geometry.location.lat()],
  components: result.address_components
    .filter((c) => c.types.includes("country"))
    .map((c) => ({
      longName: c.long_name,
      shortName: c.short_name,
      type: "country"
    })),
  id: result.place_id,
  name: getNameFromResult(result),
  raw: result,
  types: result.types
});

const search: GeoService["search"] = async (term, biasing?) => {
  if (!window.google?.maps) throw new Error("Google Maps not loaded!");

  const address = isCoordinates(term) ? undefined : term;
  const location = isCoordinates(term)
    ? {
        lng: term[0],
        lat: term[1]
      }
    : undefined;
  const bounds = biasing?.boundaries
    ? convertBoundariesToGoogleBounds(biasing?.boundaries)
    : undefined;

  const geocoder = new window.google.maps.Geocoder();
  const response = await geocoder.geocode({
    address,
    bounds,
    location
  });

  const sortedResults = response.results.sort(sortResults);

  return sortedResults.map<GeoResult>(convertGeocoderResultsToGeoResult);
};

const debouncedSearch = debounce(search, 350, {
  leading: true
});

export const googleMapsService: GeoService = {
  search,
  debouncedSearch
};
